import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { favoritesAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import logger from '../utils/logger';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return ctx;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { addToast } = useToast();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load favorites when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setFavoriteIds([]);
      return;
    }

    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const response = await favoritesAPI.getFavorites();
        if (response.success && response.data) {
          const list = response.data.favorites || [];
          const ids = response.data.favoriteIds || list.map(p => p._id);
          setFavoriteIds(ids.map(id => id.toString()));
          setFavorites(list);
        } else {
          setFavoriteIds([]);
          setFavorites([]);
        }
      } catch (error) {
        logger.error('Error loading favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [isAuthenticated]);

  const isFavorite = useCallback(
    (productId) => {
      if (!productId) return false;
      return favoriteIds.includes(productId.toString());
    },
    [favoriteIds]
  );

  const toggleFavorite = useCallback(
    async (productId, productName) => {
      if (!productId) return;

      // Require login for favorites
      if (!isAuthenticated) {
        addToast({
          type: 'info',
          message: 'Please log in to save favorites.'
        });
        return;
      }

      // Block unverified users from using favorites
      if (!user?.emailVerified) {
        addToast({
          type: 'info',
          message: 'Please verify your email first to use favorites. You can verify it in your profile settings.'
        });
        return;
      }

      // Optimistic update
      setFavoriteIds((prev) => {
        const exists = prev.includes(productId.toString());
        if (exists) {
          return prev.filter((id) => id !== productId.toString());
        }
        return [...prev, productId.toString()];
      });

      try {
        const response = await favoritesAPI.toggleFavorite(productId);
        if (response.success) {
          const isNowFavorite = response.data?.isFavorite;
          if (Array.isArray(response.data?.favoriteIds)) {
            setFavoriteIds(response.data.favoriteIds.map(id => id.toString()));
          }

          // Refresh full favorites list so profile page has up-to-date product data
          try {
            const res = await favoritesAPI.getFavorites();
            if (res.success && res.data) {
              const list = res.data.favorites || [];
              const ids = res.data.favoriteIds || list.map(p => p._id);
              setFavoriteIds(ids.map(id => id.toString()));
              setFavorites(list);
            }
          } catch (reloadErr) {
            logger.error('Error refreshing favorites after toggle:', reloadErr);
          }

          // Toast feedback
          if (typeof isNowFavorite === 'boolean') {
            addToast({
              type: 'success',
              message: isNowFavorite
                ? `Added${productName ? ` "${productName}"` : ''} to favorites`
                : `Removed${productName ? ` "${productName}"` : ''} from favorites`
            });
          }
        }
      } catch (error) {
        logger.error('Error toggling favorite:', error);
        addToast({
          type: 'error',
          message: 'Failed to update favorites. Please try again.'
        });
        // Revert by refetching
        try {
          const res = await favoritesAPI.getFavorites();
          if (res.success && res.data) {
            const list = res.data.favorites || [];
            const ids = res.data.favoriteIds || list.map(p => p._id);
            setFavoriteIds(ids.map(id => id.toString()));
            setFavorites(list);
          } else {
            setFavoriteIds([]);
            setFavorites([]);
          }
        } catch (innerErr) {
          logger.error('Error reloading favorites after failure:', innerErr);
        }
      }
    },
    [isAuthenticated, user, addToast]
  );

  const value = {
    favoriteIds,
    favorites,
    loading,
    isFavorite,
    toggleFavorite,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};


